/**
 * I18N-02 — the language a piece of community content is IN, and the room that
 * language talks in.
 *
 * The community used to be one pool: one chat room, one feed, one prayer wall,
 * every language writing into the same place. That works while everyone speaks
 * the same language and breaks the moment they don't — a Japanese sentence
 * dropped into an English room is unreadable to the room and unanswerable for
 * the person who wrote it.
 *
 * The split is by the language of the CONTENT, never by country. `cf.country` is
 * free in the Worker and is deliberately unused: a Spanish speaker in the US and
 * a Turk in Germany would both be filed under the wrong flag.
 *
 * ── What this module is, and what it is not ──────────────────────────────────
 *
 * It is a cheap, conservative classifier: Unicode script ranges first, then a
 * count of language-unique stopwords for the Latin-script languages. No AI, no
 * network, no model download — it runs on the request path for every post,
 * request and chat message, on both ends of the wire.
 *
 * It is NOT a moderation input. The stamp this produces never narrows the
 * profanity filter: see the header of ./profanityLocales.ts. A `tr`-stamped row
 * can carry English profanity and an `en` room can carry Russian, so every list
 * still runs on every message.
 *
 * ── Why "I cannot say" is a first-class answer ───────────────────────────────
 *
 * Most community writing is short — "amen 🙏", a verse reference, a link, a name.
 * A classifier forced to answer would file those at random, and the visible
 * symptom of a wrong answer is somebody's prayer vanishing from the only feed
 * they read. So {@link detectContentLang} returns `unknown` freely, and every
 * caller has a safe thing to do with it: the write path falls back to what the
 * client declared, and the wrong-room guard lets the message through.
 */

/**
 * Every language a piece of content can be stamped with — and therefore every
 * room that can exist.
 *
 * **The nine languages the app is actually translated into.** Byte-identical to
 * `PRAYER_LOCALES` and to what `apps/mobile/src/i18n/languages.ts` ships, held
 * so by a test in ./contentLang.test.ts rather than by an import, because
 * ./domain.ts already imports this module and the two would form a cycle.
 *
 * ── Two rules, and the app-language one is the outer bound ───────────────────
 *
 * 1. **A room's language must be one the app can speak.** A room is not just a
 *    bucket: it has an interface around it, system messages inside it, and push
 *    copy about it. Opening one for a language none of that exists in ships a
 *    third of a product and calls it a community.
 * 2. **A room's language must be one the profanity filter can police.** Chat
 *    never reaches the AI classifier, so the wordlists in ./profanityLocales.ts
 *    are the only gate there is. `check-locales.mjs` already asserts shipped ⊆
 *    moderated, so rule 1 gives rule 2 for free — but the direction matters and
 *    is easy to get backwards: moderated is a SUPERSET. `tr` is moderated and
 *    has no room, and that is correct, not an oversight.
 *
 * Two earlier cuts of this list were wrong, both by being wider:
 *
 * - **Seventeen**, adding script-detectable languages with no UI (`ja`, `zh`,
 *   `ar`, `he`, `hi`, `el`, `th`). The wordlists carry Latin, Cyrillic and
 *   Hangul and nothing else — zero kana, zero CJK, zero Arabic — so those seven
 *   would have been seven completely unmoderated prayer rooms, reopening the
 *   hole I18N-01 was built to close.
 * - **Ten**, `LOCALE_CODES` + English, which kept `tr`. Moderating a language
 *   is not the same as serving it: a Turkish speaker picking that room got an
 *   English interface around a Turkish room, and nothing else about the app
 *   spoke to them.
 *
 * A language we do not serve is simply not a language we file. A Japanese
 * sentence reads as `unknown`: it is stamped with whatever the client declared,
 * it is never refused from a room, and it lands where it lands — which leaves
 * that person exactly where they were before I18N-02, not locked out and not in
 * a room nobody is watching. Serving them means shipping the UI, the copy and a
 * measured wordlist; the room follows from that, never the other way round.
 */
export const CONTENT_LANGS = [
  "en",
  "es",
  "pt",
  "fr",
  "de",
  "it",
  "ru",
  "ko",
  "tl",
] as const;

export type ContentLang = (typeof CONTENT_LANGS)[number];

const CONTENT_LANG_SET = new Set<string>(CONTENT_LANGS);

/** True for a code this module can stamp, filter and open a room for. */
export function isContentLang(value: string): value is ContentLang {
  return CONTENT_LANG_SET.has(value);
}

/**
 * What a reader gets when nothing better is known — and what every row written
 * before I18N-02 is treated as. English, for the same reason
 * `DEFAULT_PRAYER_LOCALE` is: it is what every existing client already behaves
 * like, so nothing moves under anyone on the day this ships.
 */
export const DEFAULT_CONTENT_LANG: ContentLang = "en";

/**
 * The endonym for each language — what it calls ITSELF.
 *
 * Data rather than i18n keys, because a room switcher that renames every option
 * when you change the interface language is a switcher you cannot navigate: the
 * person looking for the Korean room is looking for "한국어", whatever their
 * phone is currently set to.
 */
export const CONTENT_LANG_ENDONYM: Record<ContentLang, string> = {
  en: "english",
  es: "español",
  pt: "português",
  fr: "français",
  de: "deutsch",
  it: "italiano",
  ru: "русский",
  ko: "한국어",
  tl: "tagalog",
};

/**
 * Map a device/app locale tag onto a content language.
 *
 * Takes the primary subtag, so "pt-BR", "de_DE" and "en-GB" all land where they
 * should. `fil` is folded to `tl` for the same reason `notify.ts` does it: iOS
 * reports Filipino as `fil` and every i18n library keys it `tl`, and getting it
 * wrong would silently strand Filipino users in the English room.
 */
export function contentLangOf(
  raw: string | null | undefined,
): ContentLang | null {
  const primary = raw?.trim().toLowerCase().split(/[-_]/)[0];
  if (!primary) return null;
  if (primary === "fil") return "tl";
  // Everything else — a Japanese phone, a Chinese one — is null, NOT English.
  // Null lets the caller decide and keeps the gap visible; answering "en" would
  // quietly file a language we do not serve as one we do.
  return isContentLang(primary) ? primary : null;
}

// ─── Chat rooms ──────────────────────────────────────────────────────────────

/**
 * The chat room a language talks in.
 *
 * English keeps the bare `global` id rather than becoming `global:en`, and that
 * is not cosmetic: every chat message ever written carries `roomId = "global"`,
 * so English inheriting the id means the entire history stays exactly where it
 * already is — no backfill, no migration, no "where did the room go" on the day
 * this ships. Every other language gets a fresh, empty room, which is what it
 * would have had anyway.
 */
export function chatRoomFor(lang: ContentLang): string {
  return lang === DEFAULT_CONTENT_LANG ? "global" : `global:${lang}`;
}

/** The inverse of {@link chatRoomFor} — `null` for a room id we don't own. */
export function chatRoomLang(roomId: string): ContentLang | null {
  if (roomId === "global") return DEFAULT_CONTENT_LANG;
  const suffix = roomId.startsWith("global:") ? roomId.slice(7) : null;
  return suffix && isContentLang(suffix) ? suffix : null;
}

// ─── Detection ───────────────────────────────────────────────────────────────

/**
 * What the classifier concluded.
 *
 * - `known` — confident enough to file content under `lang` and to refuse it
 *   from another language's room.
 * - `weak` — one signal and no contradiction. Good enough to stamp a row with,
 *   never good enough to reject somebody's message over.
 * - `unknown` — too short, all emoji, a bare link, or genuinely ambiguous.
 */
export type LangGuess =
  | { kind: "known"; lang: ContentLang }
  | { kind: "weak"; lang: ContentLang }
  | { kind: "unknown" };

/**
 * Scripts that identify their language on sight, no wordlist needed.
 *
 * Exactly two, because exactly two of the languages we serve are written in a
 * script of their own. Japanese, Chinese, Arabic, Hebrew, Hindi, Greek and Thai
 * were all listed here once and were removed with their rooms — see
 * {@link CONTENT_LANGS}. Detecting a language we cannot moderate, cannot talk to
 * and have nowhere to put buys nothing: the guard would refuse the message with
 * no room to offer, which is a lockout, not a redirect. They now fall through to
 * the wordlist path, match no markers, and come back `unknown` — which every
 * caller already handles as "leave it alone".
 */
const SCRIPTS: { lang: ContentLang; re: RegExp }[] = [
  { lang: "ko", re: /[가-힣ᄀ-ᇿ㄰-㆏]/g },
  { lang: "ru", re: /[Ѐ-ӿ]/g },
];

/**
 * Share of a text's letters that must belong to one script before we call it.
 *
 * Not 100%, and not close to it: real messages carry names, brand words, verse
 * references and emoji in Latin script no matter what language they are in
 * ("Ephesians 4:32 🙏 とても励まされました"). A third is comfortably above what a
 * stray Latin word contributes and comfortably below what a genuine sentence in
 * the script scores.
 */
const SCRIPT_SHARE = 0.3;

/**
 * Words that identify a language on their own, per language.
 *
 * Generous on purpose — {@link UNIQUE_MARKERS} throws away every word that
 * appears in more than one list, so listing `la` under Spanish, French and
 * Italian is not a bug, it is how a shared word gets removed. What survives is
 * the set of words that can only be one language.
 */
const STOPWORDS: Record<string, string[]> = {
  en: [
    "the",
    "and",
    "is",
    "are",
    "you",
    "your",
    "my",
    "for",
    "with",
    "this",
    "that",
    "have",
    "will",
    "from",
    "they",
    "was",
    "been",
    "would",
    "about",
    "please",
    "thank",
    "thanks",
    "just",
    "need",
    "help",
    "love",
    "bless",
    "lord",
    "pray",
    "prayer",
    "prayers",
    "praying",
    "brother",
    "brothers",
    "sister",
    "sisters",
    "today",
    "everyone",
    "friends",
    "family",
    "because",
    "what",
    "when",
    "where",
    "there",
    "here",
    "much",
    "very",
  ],
  es: [
    "el",
    "la",
    "los",
    "las",
    "un",
    "una",
    "es",
    "no",
    "que",
    "de",
    "y",
    "en",
    "con",
    "por",
    "para",
    "pero",
    "como",
    "más",
    "muy",
    "este",
    "esta",
    "esto",
    "todo",
    "hola",
    "gracias",
    "dios",
    "señor",
    "oración",
    "oraciones",
    "oren",
    "hermano",
    "hermana",
    "hermanos",
    "hermanas",
    "bendiciones",
    "bendición",
    "siempre",
    "porque",
    "cuando",
    "donde",
    "también",
    "hacer",
    "tiene",
    "ser",
    "estar",
    "nosotros",
    "ustedes",
    "favor",
    "mucho",
    "mucha",
    "muchas",
  ],
  pt: [
    "o",
    "os",
    "as",
    "um",
    "uma",
    "é",
    "não",
    "que",
    "de",
    "e",
    "em",
    "com",
    "por",
    "para",
    "mas",
    "como",
    "mais",
    "muito",
    "este",
    "esta",
    "isso",
    "tudo",
    "olá",
    "obrigado",
    "obrigada",
    "deus",
    "senhor",
    "oração",
    "orações",
    "orem",
    "irmão",
    "irmã",
    "irmãos",
    "irmãs",
    "bênçãos",
    "sempre",
    "porque",
    "quando",
    "onde",
    "também",
    "fazer",
    "você",
    "vocês",
    "então",
    "ainda",
    "vamos",
    "favor",
    "muita",
    "muitas",
    "muitos",
  ],
  fr: [
    "le",
    "les",
    "des",
    "une",
    "est",
    "et",
    "pas",
    "pour",
    "avec",
    "dans",
    "vous",
    "nous",
    "je",
    "qui",
    "que",
    "sur",
    "mais",
    "tout",
    "être",
    "ça",
    "très",
    "bien",
    "merci",
    "dieu",
    "seigneur",
    "prière",
    "prières",
    "priez",
    "prions",
    "frère",
    "frères",
    "sœur",
    "sœurs",
    "toujours",
    "parce",
    "quand",
    "aussi",
    "faire",
    "elle",
    "cette",
    "alors",
    "encore",
    "déjà",
    "beaucoup",
  ],
  de: [
    "der",
    "die",
    "das",
    "und",
    "ist",
    "nicht",
    "ich",
    "du",
    "wir",
    "mit",
    "sich",
    "auch",
    "aber",
    "eine",
    "einen",
    "dass",
    "für",
    "oder",
    "wie",
    "wird",
    "haben",
    "sein",
    "mein",
    "meine",
    "dein",
    "danke",
    "danken",
    "gott",
    "herr",
    "gebet",
    "gebete",
    "bruder",
    "brüder",
    "schwester",
    "schwestern",
    "immer",
    "weil",
    "wann",
    "machen",
    "sehr",
    "hier",
    "dort",
    "alles",
    "schon",
    "noch",
    "bitte",
    "liebe",
    "kann",
  ],
  it: [
    "il",
    "lo",
    "gli",
    "che",
    "non",
    "per",
    "con",
    "sono",
    "anche",
    "questo",
    "come",
    "della",
    "delle",
    "del",
    "nel",
    "però",
    "molto",
    "grazie",
    "sempre",
    "essere",
    "signore",
    "preghiera",
    "preghiere",
    "pregate",
    "fratello",
    "fratelli",
    "sorella",
    "sorelle",
    "perché",
    "quando",
    "dove",
    "fare",
    "tutto",
    "ancora",
    "prego",
    "bene",
    "cosa",
    "loro",
    "favore",
  ],
  tl: [
    "ang",
    "ng",
    "mga",
    "nang",
    "ako",
    "ikaw",
    "siya",
    "natin",
    "namin",
    "po",
    "hindi",
    "ay",
    "kayo",
    "tayo",
    "kami",
    "niya",
    "nila",
    "salamat",
    "panginoon",
    "diyos",
    "panalangin",
    "kapatid",
    "lahat",
    "ngayon",
    "kasi",
    "kung",
    "saan",
    "paano",
    "gusto",
    "wala",
    "meron",
    "sana",
    "talaga",
    "pero",
    "yung",
    "iyong",
    "akin",
    "atin",
    "dito",
    "doon",
    "ganda",
  ],
};

/**
 * Universally shared words that must never count for anybody.
 *
 * The uniqueness filter below removes words that appear in more than one of OUR
 * lists, but it cannot remove a word nobody bothered to list twice. "amen" is
 * the canonical example: it is the same word in all seventeen languages and it
 * is the single most common word in this app's community. Left in the English
 * list it would have quietly made every "amen 🙏" an English message.
 */
const UNIVERSAL = new Set([
  "amen",
  "alleluia",
  "hallelujah",
  "aleluya",
  "hosanna",
  "jesus",
  "christ",
  "maria",
  "israel",
  "ok",
  "okay",
  "hi",
  "hey",
  "yes",
  "no",
  "si",
  "sí",
]);

/**
 * Shortest word that may serve as evidence.
 *
 * Four, and the number is doing real work. The function words of the Romance
 * languages are almost all one to three letters — la, le, el, il, lo, de, del,
 * un, es, no, si, che, qui, con, por, per, mas, ma, se, mi, su, tu — and they
 * are precisely the words that mean four different things in four of our
 * languages. Any list that leaves them in is a list that files Italian as
 * Spanish. Cutting at four removes the entire class in one rule, and every
 * language still has plenty left: `schwester`, `bendiciones`, `panalangin`,
 * `teşekkür`, `obrigado` are the words that actually identify a language.
 *
 * The three-letter markers this costs (`ang`, `der`, `die`, `und`, `bir`) all
 * belong to languages with a dozen longer markers each, so nothing is lost that
 * the wordlist did not already say twice.
 */
const MIN_MARKER_LEN = 4;

/**
 * Strip diacritics so `está` and `esta`, `más` and `mas`, `için` and `icin` are
 * the same token.
 *
 * Not a nicety: half of the community types without accents, and an unfolded
 * list makes the accented and unaccented spellings of one word look like two
 * different languages' evidence. Folding also lets the collision filter below
 * SEE those pairs — `mas` (Portuguese "but") and `más` (Spanish "more") only
 * cancel each other out once they are spelled the same.
 */
function fold(word: string): string {
  return word.normalize("NFD").replace(/\p{M}/gu, "");
}

/**
 * Word → the one language that claims it. Built once at module load: any word
 * appearing in two or more lists is dropped, so cross-language collisions
 * ("que", "para", "porque", "pero") disappear without anybody maintaining a
 * list of them by hand.
 */
const UNIQUE_MARKERS: Map<string, ContentLang> = (() => {
  const seen = new Map<string, ContentLang | null>();
  for (const [lang, words] of Object.entries(STOPWORDS)) {
    for (const raw of words) {
      const word = fold(raw);
      if (word.length < MIN_MARKER_LEN || UNIVERSAL.has(word)) continue;
      // `null` marks "claimed by more than one language" — sticky once set, so
      // a third list cannot un-collide a word.
      seen.set(word, seen.has(word) ? null : (lang as ContentLang));
    }
  }
  const unique = new Map<string, ContentLang>();
  for (const [word, lang] of seen) if (lang) unique.set(word, lang);
  return unique;
})();

/** Shortest text the wordlist path will look at. Below this the answer is
 *  `unknown` — "thanks!" and "gracias" are not worth a wrong bucket. */
const MIN_WORD_CHARS = 12;

/**
 * Strip everything that is not prose before classifying: URLs, @mentions, and
 * verse references. All three are language-neutral and all three are common
 * enough in this app to swing a short message on their own — a Spanish post
 * quoting "John 3:16" should not read as more English for it.
 */
function prose(text: string): string {
  return text
    .replace(/https?:\/\/\S+/g, " ")
    .replace(/@[\p{L}\p{N}_]+/gu, " ")
    .replace(/\b\d+[:.]\d+\b/g, " ");
}

/**
 * Classify a piece of community text.
 *
 * Script first (it is decisive and cheap), then unique-marker counting for the
 * Latin-script languages. Ties and thin evidence return `unknown` rather than a
 * guess — see the module header for why that is the safe direction to fail in.
 */
export function detectContentLang(raw: string): LangGuess {
  const text = prose(raw ?? "");
  if (!text.trim()) return { kind: "unknown" };

  // ── Script ────────────────────────────────────────────────────────────────
  // Denominator is LETTERS, not characters: emoji, digits and punctuation are
  // language-neutral, and counting them would make "🙏🙏🙏 안녕하세요" look like
  // a mostly-neutral string instead of the Korean sentence it is.
  const letters = text.match(/\p{L}/gu)?.length ?? 0;
  if (letters > 0) {
    for (const { lang, re } of SCRIPTS) {
      re.lastIndex = 0;
      const hits = text.match(re)?.length ?? 0;
      if (hits / letters >= SCRIPT_SHARE) return { kind: "known", lang };
    }
  }

  // ── Latin-script wordlists ────────────────────────────────────────────────
  if (letters < MIN_WORD_CHARS) return { kind: "unknown" };

  const words = (text.toLowerCase().match(/[\p{L}]+/gu) ?? []).map(fold);
  const score = new Map<ContentLang, number>();
  for (const word of words) {
    const lang = UNIQUE_MARKERS.get(word);
    if (lang) score.set(lang, (score.get(lang) ?? 0) + 1);
  }
  if (score.size === 0) return { kind: "unknown" };

  const ranked = [...score.entries()].sort((a, b) => b[1] - a[1]);
  const [top, runnerUp] = ranked;
  if (!top) return { kind: "unknown" };
  const [lang, hits] = top;
  const second = runnerUp?.[1] ?? 0;

  // Two markers and a clear lead: confident enough to move content on, and to
  // refuse it from the wrong room.
  if (hits >= 2 && hits > second) return { kind: "known", lang };
  // A single marker with nothing arguing against it. Enough to file a row by,
  // never enough to reject somebody's message over.
  if (hits >= 1 && second === 0) return { kind: "weak", lang };
  return { kind: "unknown" };
}

/**
 * The language to STAMP on a row the user just wrote.
 *
 * The client declares one (its own UI/community language, the same way PRAY-03
 * ships `locale` with a prayer request) and the server checks it against the
 * text. Detection only overrides the declaration when it is `known` — a `weak`
 * guess is not worth contradicting an explicit choice, and `unknown` has nothing
 * to say at all.
 *
 * A declaration we don't recognise (an old client, a hand-rolled request) falls
 * back to detection, then to English.
 */
export function resolveContentLang(
  declared: string | null | undefined,
  text: string,
): ContentLang {
  const guess = detectContentLang(text);
  if (guess.kind === "known") return guess.lang;
  const claimed = contentLangOf(declared);
  if (claimed) return claimed;
  if (guess.kind === "weak") return guess.lang;
  return DEFAULT_CONTENT_LANG;
}

/**
 * Is this text in the wrong place? Returns the language it looks like, or null
 * to allow.
 *
 * Only a `known` guess can block, so the failure mode is "a stray foreign
 * message gets through", never "somebody's short prayer is refused because the
 * classifier could not read it". That asymmetry is the entire point: this guard
 * exists to keep a room readable, and a guard that rejects real prayers costs
 * more than the mess it prevents.
 */
export function wrongRoomLang(
  text: string,
  roomLang: ContentLang,
): ContentLang | null {
  const guess = detectContentLang(text);
  if (guess.kind !== "known") return null;
  return guess.lang === roomLang ? null : guess.lang;
}
